import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';

// Shared reactive state across all CRM components (module-level singleton)
const selectedInboxId = ref(null);

/**
 * Composable that provides CRM context (inbox selection, mode, etc.)
 *
 * When fetchData=true the calling component will:
 *  – fetch stages + deals on mount
 *  – re-fetch when the shared selectedInboxId changes
 *  – clean up its own watcher on unmount
 *
 * Multiple components can safely call this; each manages its own lifecycle.
 * Because only one route-level component is visible at a time in Vue Router
 * flat routes, duplicate concurrent fetches do not happen in practice.
 */
export function useCrmContext({ fetchData = false } = {}) {
  const store = useStore();
  const { currentAccount } = useAccount();

  const currentRole = computed(() => store.getters.getCurrentRole);
  const isAdmin = computed(() => currentRole.value === 'administrator');

  const crmMode = computed(() => currentAccount.value?.crm_mode || 'general');
  const crmInboxIds = computed(
    () => currentAccount.value?.crm_inbox_ids || []
  );

  const allInboxes = computed(() => store.getters['inboxes/getInboxes']);
  const crmInboxes = computed(() => {
    if (crmMode.value !== 'per_inbox') {
      return allInboxes.value;
    }

    // In per_inbox mode, always show only inboxes enabled in CRM settings.
    const enabledInboxIds = new Set(crmInboxIds.value.map(Number));
    return allInboxes.value.filter(inbox => enabledInboxIds.has(Number(inbox.id)));
  });

  function setSelectedInboxId(val) {
    selectedInboxId.value = val ? Number(val) : null;
  }

  function fetchCrmData() {
    const params = {};
    if (selectedInboxId.value) params.inbox_id = selectedInboxId.value;
    store.dispatch('crmStages/fetch', params);
    store.dispatch('crmDeals/fetch', params);
  }

  if (fetchData) {
    // Each component manages its own watcher instance (component-scoped)
    let stopWatcher = null;

    onMounted(() => {
      store.dispatch('inboxes/get');
      fetchCrmData();

      stopWatcher = watch(selectedInboxId, () => {
        fetchCrmData();
      });
    });

    onUnmounted(() => {
      if (stopWatcher) {
        stopWatcher();
        stopWatcher = null;
      }
    });
  }

  return {
    crmInboxId: selectedInboxId,
    crmSetInboxId: setSelectedInboxId,
    crmInboxes,
    crmMode,
    currentRole,
    isAdmin,
    fetchCrmData,
  };
}
